import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { OrganisationTypeConstants } from "../../../../constants";
import { useAuth } from "../../../../useAuth";
import {
  GanttTaskViewMode,
  TasksDashboardFilterKey,
  TasksDashboardFilters,
  TasksDashboardVariant,
  TasksDashboardView,
} from "../../../../widget";
import { TasksDashboardQueryFilters } from "./models";
import { useTasksDashboard, UseTasksDashboardReturnData } from "./useTasksDashboard";
import { applyWidgetFilter, toWidgetFilters } from "./utils";

interface TasksDashboardContextType extends UseTasksDashboardReturnData {
  /** Design system variant — Seed for the Asset Manager portal, Hub for everyone else. */
  variant: TasksDashboardVariant;
  view: TasksDashboardView;
  onViewChange: (view: TasksDashboardView) => void;
  /** The Gantt's own Timeline/Agenda choice, as the chart reports it — it selects which query loads. */
  ganttViewMode: GanttTaskViewMode;
  onGanttViewModeChange: (viewMode: GanttTaskViewMode) => void;
  hideCompleted: boolean;
  onHideCompletedChange: (hideCompleted: boolean) => void;
  hideUnassigned: boolean;
  onHideUnassignedChange: (hideUnassigned: boolean) => void;
  /** The one shared task-level filter object passed to every query (BR-02828). */
  filters: TasksDashboardQueryFilters;
  setFilter: <K extends keyof TasksDashboardQueryFilters>(key: K, value: TasksDashboardQueryFilters[K]) => void;
  /** The same selections in the dashboard header's shape, plus its change handler. */
  widgetFilters: TasksDashboardFilters;
  onFilterChange: (key: TasksDashboardFilterKey, values: string[]) => void;
}

export const TasksDashboardContext = createContext<TasksDashboardContextType>({} as TasksDashboardContextType);

interface TasksDashboardProviderProps {
  children?: JSX.Element;
}

/**
 * Data boundary for the Tasks dashboard route. It owns the session-only view / filter / toggle
 * state and the org-wide task feed, so the page shell and the panels and modals the wiring tickets
 * add (KAN-9562/63/64/65) can read them from context instead of threading props down.
 */
export const TasksDashboardProvider = ({ children }: TasksDashboardProviderProps): JSX.Element => {
  const { currentUserType } = useAuth();

  // View choice and the header toggles are session-only: not persisted and not a URL param.
  const [view, setView] = useState<TasksDashboardView>("kanban");
  // The chart owns its Timeline/Agenda toggle and reports it up; mirrored here only to pick the query.
  const [ganttViewMode, setGanttViewMode] = useState<GanttTaskViewMode>("timeline");
  // Unassigned starts hidden so the dashboard opens focused on tasks that are assigned to someone;
  // flipping the header toggle brings the lane (and its query) back for the rest of the session.
  const [filters, setFilters] = useState<TasksDashboardQueryFilters>({ hideUnassigned: true });

  /*
    A task nobody owns can't match an assignee, so an assignee selection leaves the Unassigned lane
    permanently empty — it is hidden while that selection stands, and none of the queries ask for it.
    Derived rather than written into `filters`, so the user's own toggle choice survives underneath and
    is handed back the moment the assignee filter is cleared. The header hides the toggle itself on the
    same condition, since it would then have nothing to decide.
  */
  const queryFilters = useMemo<TasksDashboardQueryFilters>(
    () => (filters.assigneeUuids?.length ? { ...filters, hideUnassigned: true } : filters),
    [filters]
  );

  const hideCompleted = Boolean(queryFilters.hideCompleted);
  const hideUnassigned = Boolean(queryFilters.hideUnassigned);

  // Held as state so its identity is stable between filter changes — the hook refetches every lane
  // whenever this object changes.
  const setFilter = useCallback(
    <K extends keyof TasksDashboardQueryFilters>(key: K, value: TasksDashboardQueryFilters[K]): void => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  /*
    The header's selections are folded into the one shared query filter, so picking a filter re-issues
    every query rather than narrowing the loaded cards — which is what keeps the server's lane and
    group counts describing the rows on screen (BR-02828).
  */
  const onFilterChange = useCallback((key: TasksDashboardFilterKey, values: string[]): void => {
    setFilters((prev) => applyWidgetFilter(prev, key, values));
  }, []);

  /*
    The hide toggles live in the same object for the same reason: they change which tasks are asked
    for — the Kanban stops querying the hidden lane, and the group counts cover only the remaining
    statuses — rather than hiding rows the queries already counted.
  */
  const onHideCompletedChange = useCallback((hide: boolean): void => {
    setFilters((prev) => ({ ...prev, hideCompleted: hide }));
  }, []);

  const onHideUnassignedChange = useCallback((hide: boolean): void => {
    setFilters((prev) => ({ ...prev, hideUnassigned: hide }));
  }, []);

  const tasksDashboard = useTasksDashboard({ view, ganttViewMode, filters: queryFilters });

  /*
    Seed and Hub are the two products (see the logon form's product mapping): Asset Manager is Seed,
    every other organisation type is Hub. The variant carries the geometry — Hub is square, Seed is
    rounded. It deliberately does not carry the typeface: Development Manager is a Hub product drawn
    in Figtree, which the persona-scoped --hub-font-family handles instead.
  */
  const variant: TasksDashboardVariant = currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "seed" : "hub";

  const value = useMemo<TasksDashboardContextType>(
    () => ({
      ...tasksDashboard,
      variant,
      view,
      onViewChange: setView,
      ganttViewMode,
      onGanttViewModeChange: setGanttViewMode,
      hideCompleted,
      onHideCompletedChange,
      hideUnassigned,
      onHideUnassignedChange,
      // What the queries actually ran with; `widgetFilters` stays the user's own selections, which is
      // what the header renders (and what an assignee filter must not appear to have changed).
      filters: queryFilters,
      setFilter,
      widgetFilters: toWidgetFilters(filters),
      onFilterChange,
    }),
    [
      tasksDashboard,
      variant,
      view,
      ganttViewMode,
      hideCompleted,
      hideUnassigned,
      onHideCompletedChange,
      onHideUnassignedChange,
      filters,
      queryFilters,
      setFilter,
      onFilterChange,
    ]
  );

  return <TasksDashboardContext.Provider value={value}>{children}</TasksDashboardContext.Provider>;
};

export const useTasksDashboardContext = (): TasksDashboardContextType => useContext(TasksDashboardContext);
